import React, { useMemo, useState } from 'react';
import { ButtonV2, Flex, IconV2, SpacingStack, TextV2 } from '@withjoy/joykit';
import { useHistory, useRouterHelper, useTranslation } from '@shared/core';

import { useShoppingCart } from '@apps/registry/common/components/ShoppingCart/state';
import type { InCartProduct } from '@apps/registry/common/components/ShoppingCart/ShoppingCart.types';
import { CartCheckoutSteps } from '@apps/registry/common/components/ShoppingCart/ShoppingCart.types';
import { CartSections } from '@apps/registry/common/components/ShoppingCart/ShoppingCart.constants';

import {
  CartFooterAtomsContainer,
  ShippingAddressContainer,
  ShippingAddressText,
  styles,
  SubtotalTextContainer,
  TotalTextContainer,
  CartFooterAtomsWrapper,
} from './CartFooterAtoms.styles';
import { Shipping } from '../MetadataAtoms';
import { CartFooterSubmitButton } from './CartFooterSumbitButton';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';
import { SummaryItemListContainer } from '../SummaryItemList';
import { useGiftWrapECardPrice } from '@shared/components/GiftWrap';
import { LoadingExpressCheckoutElement } from '@shared/core/stripe';
import { useShoppingCartTelemetry } from '../../ShoppingCart.telemetry';
import { useRoutePaths } from '@apps/registry/guest/GuestRegistry.routes';
import { interpretShippingDecimalString } from '@apps/registry/common/utils/shippingAddress';
import { LoadingRegistryCheckoutSettings } from '../../../LoadingRegistryCheckoutSettings';
import { useEventInfo } from '@shared/utils/eventInfo';

type Props = {
  subtotal?: string;
  total?: string;
  inCartProductList?: Array<InCartProduct>;
  inCartProductTotal?: number;
  deliveryTotal?: number;
  salesTaxTotal?: number;
  registryCurrencyCode?: string;
  saveData?: () => void;
  hasECard?: boolean;
  disableNextStep?: boolean;
  expressCheckoutEnabled?: boolean;
};

export const CartFooterAtoms = ({
  subtotal,
  total,
  inCartProductList,
  inCartProductTotal,
  registryCurrencyCode,
  hasECard,
  saveData,
  disableNextStep,
  expressCheckoutEnabled,
}: Props) => {
  const { t } = useTranslation('sharedRegistry');
  const { cartFooterAddressText, subtotal: subtotalText, modifyButton, total: totalText, closeOrderSummary } = t('shoppingCart');
  const { shipping, freeShipping } = t('shoppingCart', 'shippingAddressStep');
  const isMobileOrTablet = useIsMobileOrTablet();
  const registryGiftWrapEcardPrice = useGiftWrapECardPrice();
  const telemetry = useShoppingCartTelemetry();
  const {
    state: { currentCheckoutStep, shippingAddress, shippingFee, itemList },
    mutators: { updateCurrentSection },
  } = useShoppingCart();
  const showExpressCheckout = Boolean(expressCheckoutEnabled && itemList.length && currentCheckoutStep === CartCheckoutSteps.Cart);
  const routePaths = useRoutePaths();
  const router = useRouterHelper();
  const history = useHistory();
  const { eventInfo } = useEventInfo();
  const eventId = eventInfo?.eventId;

  const getShippingMethodText = () => (interpretShippingDecimalString(shippingFee).isFreeShipping ? freeShipping() : shipping());
  const [isSummaryOpen, setIsSummaryOpen] = useState(false);
  const checkoutFallback = (
    <CartFooterSubmitButton
      saveData={saveData}
      disabled={disableNextStep}
    />
  );

  const totalWithECard = useMemo(() => {
    if (!total || !hasECard) {
      return total;
    }
    const totalParsed = parseFloat(total.substring(1));
    return `$${totalParsed + registryGiftWrapEcardPrice}`;
  }, [hasECard, registryGiftWrapEcardPrice, total]);

  return (
    <CartFooterAtomsContainer __css={styles.cartFooterAtomsContainerStyles(isSummaryOpen)}>
      {isMobileOrTablet && currentCheckoutStep !== CartCheckoutSteps.Cart && inCartProductList && inCartProductTotal && (
        <SummaryItemListContainer
          inCartProductList={inCartProductList}
          inCartProductTotal={inCartProductTotal}
          registryCurrencyCode={registryCurrencyCode}
          isOpen={isSummaryOpen}
          setIsOpen={setIsSummaryOpen}
          hasECard={hasECard}
        />
      )}
      <CartFooterAtomsWrapper __css={styles.cartFooterAtomsWrapperStyles(isMobileOrTablet)}>
        {currentCheckoutStep === CartCheckoutSteps.Cart && (
          <ShippingAddressContainer __css={styles.shippingAddressContainerStyles}>
            <Flex alignItems="center">
              <IconV2
                size="sm"
                marginRight={3}
              >
                <Shipping />
              </IconV2>
              <ShippingAddressText __css={styles.shippingAddressTextStyles}>
                {cartFooterAddressText({ shippingMethod: getShippingMethodText(), addressName: shippingAddress?.name })}
              </ShippingAddressText>
            </Flex>
            <ShippingAddressText
              __css={styles.shippingAddressButtonStyles}
              onClick={() => updateCurrentSection(CartSections.ADDRESS)}
            >
              {modifyButton()}
            </ShippingAddressText>
          </ShippingAddressContainer>
        )}
        {subtotal && (
          <SubtotalTextContainer __css={styles.subtotalTextContainerStyles}>
            <TextV2 typographyVariant="hed1">{subtotalText()}</TextV2>
            <TextV2 typographyVariant="hed1">{subtotal}</TextV2>
          </SubtotalTextContainer>
        )}
        {total && (
          <TotalTextContainer __css={styles.totalTextContainerStyles}>
            <TextV2 typographyVariant="hed4">{totalText()}</TextV2>
            <TextV2 typographyVariant="hed4">{totalWithECard}</TextV2>
          </TotalTextContainer>
        )}
        <SpacingStack spacing={3}>
          {isSummaryOpen ? (
            <ButtonV2
              intent="neutral"
              fullWidth
              onClick={() => setIsSummaryOpen(false)}
            >
              {closeOrderSummary()}
            </ButtonV2>
          ) : (
            !showExpressCheckout && (
              <CartFooterSubmitButton
                saveData={saveData}
                disabled={disableNextStep}
              />
            )
          )}
          {showExpressCheckout && (
            <LoadingRegistryCheckoutSettings
              eventId={eventId}
              fallbackContent={checkoutFallback}
            >
              <LoadingExpressCheckoutElement
                onParentClick={event => {
                  telemetry.expressCheckoutClicked(event);
                  history.push(router.buildPath(routePaths.cartCheckout.goToPath()));
                }}
                disablePaymentEvents={true}
                onConfirm={() => {}}
                onReady={event => telemetry.expressCheckoutElementReady(event)}
                options={{ buttonHeight: 50, buttonType: { applePay: 'buy', googlePay: 'checkout' } }}
                fallbackContent={checkoutFallback}
                upperContent={
                  <CartFooterSubmitButton
                    saveData={saveData}
                    disabled={disableNextStep}
                    variant={'outline'}
                  />
                }
              />
            </LoadingRegistryCheckoutSettings>
          )}
        </SpacingStack>
      </CartFooterAtomsWrapper>
    </CartFooterAtomsContainer>
  );
};
